import { createChatMessage, createAttachmentMetadata, ChatServiceError, decodeChatToolArguments, createChatToolDefinition, chatToolParameters, MultiBarToolActivityProjection, createMultiBarChatMessage, MultiBarToolActivityLabeler, validateChatToolUserInputRequest, validateChatToolUserInputResponse, encodeChatToolUserInputResultEnvelope, createCancelledChatToolUserInputResponse, chatToolErrorResult } from './chunk-H465XLEU.js';
import { createMessage, tokenUsage, createAttachment, AIProvider, providerDisplayName, StreamingError, providerIsOnDevice, toolResultMessage } from 'axon-provider-kit/core';
import { StreamingClient } from 'axon-provider-kit/client';

function chatMessageToAxonMessage(message) {
  return createMessage({
    id: message.id,
    role: messageRoleToAxonRole(message.role),
    content: message.content,
    timestamp: message.createdAt,
    ...message.metadata?.tokenCount !== void 0 && {
      tokens: tokenUsage(0, message.metadata.tokenCount)
    },
    ...message.metadata?.modelId !== void 0 && { modelName: message.metadata.modelId },
    ...message.metadata?.providerId !== void 0 && { providerName: message.metadata.providerId },
    ...message.metadata?.attachments !== void 0 && {
      attachments: message.metadata.attachments.map(attachmentMetadataToAxonAttachment)
    },
    ...message.metadata?.toolCalls !== void 0 && {
      toolCalls: message.metadata.toolCalls.map((toolCall) => ({
        id: toolCall.id,
        name: toolCall.name,
        arguments: toolCall.arguments,
        ...toolCall.thoughtSignature !== void 0 && {
          thoughtSignature: toolCall.thoughtSignature
        }
      }))
    },
    ...message.metadata?.toolCallId !== void 0 && { toolCallId: message.metadata.toolCallId },
    ...message.metadata?.toolName !== void 0 && { toolName: message.metadata.toolName },
    ...message.metadata !== void 0 && { providerMetadata: {
      responsesOutputJSON: message.metadata.responsesOutputJSON,
      responsesProvider: message.metadata.responsesProvider,
      anthropicContentJSON: message.metadata.anthropicContentJSON,
      appleResponseTranscriptJSON: message.metadata.appleResponseTranscriptJSON
    } }
  });
}
function axonMessageToChatMessage(message) {
  return createChatMessage({
    id: message.id,
    role: axonRoleToMessageRole(message.role),
    content: message.content,
    createdAt: message.timestamp,
    metadata: {
      modelId: message.modelName,
      providerId: message.providerName,
      tokenCount: message.tokens?.total,
      responsesOutputJSON: message.providerMetadata?.responsesOutputJSON,
      responsesProvider: message.providerMetadata?.responsesProvider,
      anthropicContentJSON: message.providerMetadata?.anthropicContentJSON,
      appleResponseTranscriptJSON: message.providerMetadata?.appleResponseTranscriptJSON,
      toolCalls: message.toolCalls?.map((toolCall) => ({
        id: toolCall.id,
        name: toolCall.name,
        arguments: toolCall.arguments,
        thoughtSignature: toolCall.thoughtSignature
      })),
      toolCallId: message.toolCallId,
      toolName: message.toolName,
      attachments: message.attachments?.map(axonAttachmentToAttachmentMetadata)
    }
  });
}
function messageRoleToAxonRole(role) {
  return role;
}
function axonRoleToMessageRole(role) {
  return role;
}
function attachmentMetadataToAxonAttachment(attachment) {
  return createAttachment({
    id: attachment.id,
    type: attachmentTypeToAxonAttachmentType(attachment.type),
    ...attachment.storageURL !== void 0 && { url: attachment.storageURL },
    ...attachment.base64 !== void 0 && { base64: attachment.base64 },
    name: attachment.name,
    mimeType: attachment.mimeType
  });
}
function axonAttachmentToAttachmentMetadata(attachment) {
  return createAttachmentMetadata({
    id: attachment.id,
    type: axonAttachmentTypeToAttachmentType(attachment.type),
    name: attachment.name ?? "Attachment",
    mimeType: attachment.mimeType ?? "application/octet-stream",
    byteCount: decodedByteCount(attachment.base64),
    ...attachment.url !== void 0 && { storageURL: attachment.url },
    ...attachment.type === "image" && attachment.base64 !== void 0 && {
      previewBase64: attachment.base64
    },
    ...attachment.base64 !== void 0 && { base64: attachment.base64 }
  });
}
function attachmentTypeToAxonAttachmentType(type) {
  return type;
}
function axonAttachmentTypeToAttachmentType(type) {
  return type;
}
function axonProviderForModel(model) {
  switch (model.providerId.toLowerCase()) {
    case "meta":
      return AIProvider.meta;
    case "venice":
      return AIProvider.venice;
    case "appleintelligence":
    case "applefoundation":
      return AIProvider.appleIntelligence;
    case "anthropic":
      return AIProvider.anthropic;
    case "openai":
      return AIProvider.openai;
    case "google":
    case "gemini":
      return AIProvider.gemini;
    case "xai":
    case "grok":
      return AIProvider.xai;
    case "perplexity":
      return AIProvider.perplexity;
    case "deepseek":
      return AIProvider.deepseek;
    case "mistral":
      return AIProvider.mistral;
    case "minimax":
      return AIProvider.minimax;
    case "zai":
    case "z.ai":
      return AIProvider.zai;
    default:
      return void 0;
  }
}
function axonModelId(model) {
  return model.modelId;
}
function axonProviderToModalProvider(provider) {
  return {
    id: provider,
    displayName: providerDisplayName(provider),
    description: ""
  };
}
function streamingErrorToChatServiceError(error) {
  switch (error.kind) {
    case "connectionFailed":
      return ChatServiceError.networkError(error);
    case "providerError":
      return ChatServiceError.unknown(error.message);
    case "parseError":
      return ChatServiceError.invalidResponse();
    case "timeout":
      return ChatServiceError.unknown("Request timed out");
    case "cancelled":
      return ChatServiceError.cancelled();
    case "unsupportedProvider":
      return ChatServiceError.unknown(error.message);
  }
}
function axonToolCallToChatMetadata(toolCall) {
  return {
    id: toolCall.id,
    name: toolCall.name,
    arguments: toolCall.arguments,
    thoughtSignature: toolCall.thoughtSignature
  };
}
function decodedByteCount(base64) {
  if (!base64) {
    return 0;
  }
  try {
    return Uint8Array.from(atob(base64), (character) => character.charCodeAt(0)).byteLength;
  } catch {
    return 0;
  }
}

// src/axon-adapter/tool-conversions.ts
function chatToolDefinitionToAxonTool(definition) {
  return {
    name: definition.name,
    description: definition.description,
    parameters: {
      type: "object",
      properties: Object.fromEntries(
        Object.entries(definition.parameters.properties).map(([key, property]) => [
          key,
          chatToolPropertyToAxonProperty(property)
        ])
      ),
      required: definition.parameters.required
    }
  };
}
function toolCallToChatToolRequest(toolCall) {
  const trimmed = toolCall.arguments.trim();
  if (trimmed.length === 0) {
    return { callId: toolCall.id, toolName: toolCall.name, arguments: {} };
  }
  try {
    return {
      callId: toolCall.id,
      toolName: toolCall.name,
      arguments: decodeChatToolArguments(trimmed)
    };
  } catch {
    return void 0;
  }
}
function chatToolPropertyToAxonProperty(property) {
  return {
    type: chatToolPropertyTypeToAxonType(property.type),
    description: property.description,
    enumValues: property.enumValues,
    items: property.items ? chatToolItemsToAxonItems(property.items) : void 0
  };
}
function chatToolPropertyTypeToAxonType(type) {
  return type;
}
function chatToolItemsToAxonItems(items) {
  return { type: chatToolPropertyTypeToAxonType(items.type) };
}
var recoverySteeringPrompt = "The previous tool-call turn was structurally invalid and has been removed from model context. Continue from the last user-visible request; call tools again if needed.";
function sanitizedForProviderContext(messages) {
  const sanitized = [];
  let index = 0;
  while (index < messages.length) {
    const message = messages[index];
    if (!message) {
      break;
    }
    if (message.role === "tool") {
      index += 1;
      continue;
    }
    if (message.role !== "assistant" || !message.toolCalls || message.toolCalls.length === 0) {
      sanitized.push(message);
      index += 1;
      continue;
    }
    const exchange = [message];
    const expectedToolCallIds = new Set(message.toolCalls.map((toolCall) => toolCall.id));
    let scanIndex = index + 1;
    while (scanIndex < messages.length) {
      const candidate = messages[scanIndex];
      if (!candidate || candidate.role !== "tool" || !candidate.toolCallId || !expectedToolCallIds.has(candidate.toolCallId)) {
        break;
      }
      exchange.push(candidate);
      expectedToolCallIds.delete(candidate.toolCallId);
      scanIndex += 1;
      if (expectedToolCallIds.size === 0) {
        break;
      }
    }
    if (expectedToolCallIds.size === 0) {
      sanitized.push(...exchange);
    } else if (message.content.trim().length > 0) {
      sanitized.push(messageWithoutToolCalls(message));
    }
    index = scanIndex;
  }
  return sanitized;
}
function removingLastResponseAttempt(messages) {
  const trimmed = [...messages];
  while (trimmed.at(-1)?.role === "tool") {
    trimmed.pop();
  }
  const lastAssistantIndex = findLastIndex(trimmed, (message) => message.role === "assistant");
  if (lastAssistantIndex === -1) {
    return trimmed;
  }
  return trimmed.slice(0, lastAssistantIndex);
}
function isInvalidToolOrderingError(error) {
  if (error instanceof StreamingError && error.kind === "providerError") {
    return error.statusCode === 400 && isInvalidToolOrderingMessage(error.message);
  }
  return isInvalidToolOrderingMessage(error instanceof Error ? error.message : String(error));
}
function messageWithoutToolCalls(message) {
  return createMessage({
    id: message.id,
    role: message.role,
    content: message.content,
    timestamp: message.timestamp,
    ...message.tokens !== void 0 && { tokens: message.tokens },
    ...message.modelName !== void 0 && { modelName: message.modelName },
    ...message.providerName !== void 0 && { providerName: message.providerName },
    ...message.attachments !== void 0 && { attachments: message.attachments },
    ...message.reasoning !== void 0 && { reasoning: message.reasoning },
    ...message.toolCallId !== void 0 && { toolCallId: message.toolCallId },
    ...message.toolName !== void 0 && { toolName: message.toolName },
    ...message.providerMetadata !== void 0 && { providerMetadata: message.providerMetadata }
  });
}
function isInvalidToolOrderingMessage(message) {
  const lowercased = message.toLowerCase();
  return lowercased.includes("messages with role 'tool'") || lowercased.includes('messages with role "tool"') || lowercased.includes("must be a response to a preceding message") || lowercased.includes("tool_calls");
}
function findLastIndex(values, predicate) {
  for (let index = values.length - 1; index >= 0; index -= 1) {
    const value = values[index];
    if (value !== void 0 && predicate(value)) {
      return index;
    }
  }
  return -1;
}
async function withTimeout(seconds, operation) {
  const controller = new AbortController();
  let timeout;
  try {
    return await Promise.race([
      operation(controller.signal),
      new Promise((_resolve, reject) => {
        timeout = setTimeout(() => {
          controller.abort();
          reject(StreamingError.timeout());
        }, seconds * 1e3);
      })
    ]);
  } finally {
    if (timeout) {
      clearTimeout(timeout);
    }
  }
}
async function* withInactivityTimeout(seconds, upstream) {
  const iterator = upstream[Symbol.asyncIterator]();
  while (true) {
    const result = await Promise.race([
      iterator.next(),
      timeoutAfter(seconds)
    ]);
    if (result.done) {
      return;
    }
    yield result.value;
  }
}
function isRetryableStreamError(error) {
  if (error instanceof StreamingError) {
    switch (error.kind) {
      case "timeout":
      case "connectionFailed":
        return true;
      case "providerError":
        return error.statusCode === 429 || error.statusCode !== void 0 && error.statusCode >= 500 && error.statusCode <= 599;
      case "cancelled":
      case "parseError":
      case "unsupportedProvider":
        return false;
    }
  }
  if (error instanceof DOMException && error.name === "TimeoutError") {
    return true;
  }
  if (error instanceof Error) {
    const lowercased = error.message.toLowerCase();
    return [
      "timed out",
      "timeout",
      "network connection lost",
      "not connected to internet",
      "cannot connect to host",
      "cannot find host",
      "dns"
    ].some((needle) => lowercased.includes(needle));
  }
  return false;
}
function timeoutAfter(seconds) {
  return new Promise((_resolve, reject) => {
    setTimeout(() => reject(StreamingError.timeout()), seconds * 1e3);
  });
}
var AxonChatService = class {
  streamingClient;
  apiKeys = /* @__PURE__ */ new Map();
  abortController;
  constructor(streamingClient = new StreamingClient()) {
    this.streamingClient = streamingClient;
  }
  async *sendMessage(message, history, model, systemPrompt) {
    for await (const event of this.sendMessageEvents(message, history, model, systemPrompt)) {
      if (event.type === "textDelta") yield event.text;
    }
  }
  async *sendMessageEvents(message, conversationHistory, model, systemPrompt) {
    this.abortController = new AbortController();
    const axonProvider = axonProviderForModel(model);
    if (!axonProvider) {
      throw ChatServiceError.unknown(`Unknown provider: ${model.providerId}`);
    }
    const apiKey = this.apiKeys.get(model.providerId);
    if (!apiKey) {
      throw ChatServiceError.missingAPIKey(axonProvider);
    }
    const axonMessages = [
      ...conversationHistory.map(chatMessageToAxonMessage),
      chatMessageToAxonMessage({ id: crypto.randomUUID(), role: "user", content: message, createdAt: /* @__PURE__ */ new Date() })
    ];
    for await (const event of this.streamingClient.stream({
      messages: axonMessages,
      provider: axonProvider,
      model: axonModelId(model),
      apiKey,
      system: systemPrompt,
      signal: this.abortController.signal
    })) {
      switch (event.type) {
        case "textDelta":
          yield { type: "textDelta", text: event.text };
          break;
        case "completion":
          yield { type: "completion", metadata: {
            modelId: model.modelId,
            providerId: model.providerId,
            tokenCount: event.completion.tokens?.total,
            responsesOutputJSON: event.completion.providerMetadata?.responsesOutputJSON,
            responsesProvider: event.completion.providerMetadata?.responsesProvider,
            anthropicContentJSON: event.completion.providerMetadata?.anthropicContentJSON,
            appleResponseTranscriptJSON: event.completion.providerMetadata?.appleResponseTranscriptJSON
          } };
          break;
        case "error":
          throw streamingErrorToChatServiceError(event.error);
        case "reasoningDelta":
        case "toolCallStart":
        case "toolCallDelta":
        case "toolCallComplete":
        case "toolResultRequired":
          break;
        default:
          throw ChatServiceError.unknown(`Unhandled streaming event: ${String(event.type)}`);
      }
    }
  }
  isConfigured(providerId) {
    return this.apiKeys.has(providerId);
  }
  setAPIKey(key, providerId) {
    this.apiKeys.set(providerId, key);
  }
  getAPIKey(providerId) {
    return this.apiKeys.get(providerId);
  }
  cancelCurrentRequest() {
    this.abortController?.abort();
    this.abortController = void 0;
  }
};
function unknownErrorToChatServiceError(error) {
  if (error instanceof ChatServiceError) {
    return error;
  }
  if (error instanceof StreamingError) {
    return streamingErrorToChatServiceError(error);
  }
  return error instanceof Error ? ChatServiceError.unknown(error.message) : ChatServiceError.unknown(String(error));
}

// src/axon-adapter/chat-tool-discovery.ts
var fail = (message) => {
  throw new Error(message);
};
var object = (v) => !!v && typeof v === "object" && !Array.isArray(v);
var ChatToolDiscovery = class _ChatToolDiscovery {
  constructor(counter) {
    this.counter = counter;
  }
  counter;
  static names = /* @__PURE__ */ new Set(["search_tools", "inspect_tool", "invoke_tool", "read_tool_result"]);
  static auxiliaryNames = /* @__PURE__ */ new Set(["search_tools", "inspect_tool", "read_tool_result"]);
  static guidance = "Answer ordinary conversation directly. Use tools only when app data or actions are needed. Find app tools with search_tools, inspect_tool for the complete argument contract, then invoke_tool with a JSON object encoded as arguments_json. Inspect every contract page before invoking. Use read_tool_result for more result pages; never repeat an action to fetch its output. Foreground clarification tools are called directly.";
  userID;
  conversationID;
  scopeRevision = 0;
  results = /* @__PURE__ */ new Map();
  excludedCallIDs = /* @__PURE__ */ new Set();
  get hasScope() {
    return this.userID !== void 0 && this.conversationID !== void 0;
  }
  configureScope(userID, conversationID, messages) {
    if (this.userID !== userID || this.conversationID !== conversationID) {
      this.scopeRevision += 1;
      if (this.userID !== void 0 || this.conversationID !== void 0) {
        for (const message of messages) {
          if (message.toolCallId) this.excludedCallIDs.add(message.toolCallId);
          for (const call of message.toolCalls ?? []) this.excludedCallIDs.add(call.id);
        }
      }
      this.results.clear();
    }
    this.userID = userID;
    this.conversationID = conversationID;
    this.rebuild(messages);
  }
  rehome(conversationID, messages) {
    if (this.userID === void 0) return;
    this.conversationID = conversationID;
    this.results = new Map([...this.results].map(([key, value]) => [key, { ...value, conversationID }]));
    this.rebuild(messages);
  }
  restore(messages) {
    this.excludedCallIDs.clear();
    this.results.clear();
    this.rebuild(messages);
  }
  rebuild(messages) {
    const previous = this.results;
    this.results = /* @__PURE__ */ new Map();
    const names = /* @__PURE__ */ new Map();
    for (const message of messages) {
      for (const call of message.toolCalls ?? []) {
        if (_ChatToolDiscovery.auxiliaryNames.has(call.name)) continue;
        if (call.name === "invoke_tool") {
          try {
            const name = decodeChatToolArguments(call.arguments)["name"];
            if (typeof name === "string") names.set(call.id, name);
          } catch {
          }
        } else names.set(call.id, call.name);
      }
      const callID = message.toolCallId;
      const target = callID ? names.get(callID) : void 0;
      if (message.role !== "tool" || !callID || !target || this.excludedCallIDs.has(callID) || !this.hasScope) continue;
      const handle = `result-${callID}`;
      const old = previous.get(handle);
      this.results.set(handle, {
        userID: this.userID,
        conversationID: this.conversationID,
        handle,
        targetTool: target,
        content: message.content,
        isError: old !== void 0 && old.userID === this.userID && old.conversationID === this.conversationID ? old.isError : errorContent(message.content)
      });
    }
  }
  static get definitions() {
    const string = (description) => ({ type: "string", description });
    return [
      createChatToolDefinition({ name: "search_tools", displayName: "Search tools", category: "Tool Discovery", description: "Search available app tools. Returns at most five summaries and a continuation cursor.", parameters: chatToolParameters({ query: string("Task or exact tool name"), cursor: string("Opaque cursor from previous page") }, ["query"]) }),
      createChatToolDefinition({ name: "inspect_tool", displayName: "Inspect tool", category: "Tool Discovery", description: "Read one tool's complete argument contract across all pages before invoking it.", parameters: chatToolParameters({ name: string("Exact tool name"), cursor: string("Opaque cursor from previous page") }, ["name"]) }),
      createChatToolDefinition({ name: "invoke_tool", displayName: "Use tool", category: "Tool Discovery", description: "Invoke an inspected app tool. Existing permissions and approvals apply. Returns a result handle and first page.", isReadOnly: false, parameters: chatToolParameters({ name: string("Exact tool name"), arguments_json: string("JSON object matching the inspected argument contract") }, ["name", "arguments_json"]) }),
      createChatToolDefinition({ name: "read_tool_result", displayName: "Read tool result", category: "Tool Discovery", description: "Read a saved result page without executing the action again.", parameters: chatToolParameters({ handle: string("Result handle"), cursor: string("Opaque cursor from previous page") }, ["handle"]) })
    ];
  }
  definitionsFor(registry) {
    return [..._ChatToolDiscovery.definitions, ...registry.toolDefinitions.filter((t) => t.isEnabled && t.executionMode === "foregroundUserInput" && !_ChatToolDiscovery.names.has(t.name))];
  }
  catalog(registry) {
    return registry.toolDefinitions.filter((t) => t.isEnabled && t.executionMode !== "foregroundUserInput" && !_ChatToolDiscovery.names.has(t.name)).sort((a, b) => a.name.localeCompare(b.name));
  }
  tool(name, registry) {
    return this.catalog(registry).find((t) => t.name === name) ?? fail("Tool is unknown, disabled, recursive, or requires direct foreground input.");
  }
  static validate(args, parameters) {
    if (Object.keys(args).some((k) => !Object.hasOwn(parameters.properties, k)) || parameters.required.some((k) => args[k] == null)) fail("Unknown or missing required arguments. Inspect the tool contract and retry.");
    const matches = (v, type) => type === "integer" ? typeof v === "number" && Number.isInteger(v) : type === "number" ? typeof v === "number" && Number.isFinite(v) : type === "array" ? Array.isArray(v) : type === "object" ? object(v) : typeof v === type;
    for (const [key, value] of Object.entries(args)) {
      const property = parameters.properties[key];
      if (!matches(value, property.type)) fail(`Invalid argument type for ${key}.`);
      if (property.enumValues && typeof value === "string" && !property.enumValues.includes(value)) fail(`Invalid enum value for ${key}.`);
      if (Array.isArray(value) && property.items && !value.every((v) => matches(v, property.items.type))) fail(`Invalid array item type for ${key}.`);
    }
  }
  async execute(request, registry) {
    if (!this.hasScope) fail("Tool discovery requires an authenticated conversation scope.");
    const definition = _ChatToolDiscovery.definitions.find((d) => d.name === request.toolName) ?? fail("Unknown discovery tool.");
    _ChatToolDiscovery.validate(request.arguments, definition.parameters);
    const str = (key) => typeof request.arguments[key] === "string" ? request.arguments[key] : "";
    let content;
    switch (request.toolName) {
      case "search_tools": {
        const query = str("query").trim().toLowerCase();
        const terms = query.split(/[^\p{L}\p{N}]+/u).filter(Boolean);
        const candidates = this.catalog(registry).map((tool) => {
          const searchable = `${tool.name} ${tool.displayName} ${tool.category} ${tool.description}`.toLowerCase();
          return { tool, score: tool.name.toLowerCase() === query ? 1e4 : terms.filter((t) => searchable.includes(t)).length };
        }).filter((t) => !query || t.score > 0).sort((a, b) => b.score - a.score || a.tool.name.localeCompare(b.tool.name));
        const scope = `search:${query}`;
        const offset = cursorOffset(str("cursor"), scope);
        if (offset > candidates.length) fail("Invalid search cursor.");
        const rows = candidates.slice(offset, offset + 5).map(({ tool: t }) => ({ name: t.name, category: t.category, summary: [...t.description].slice(0, 120).join(""), requires_approval: String(t.requiresApproval), read_only: String(t.isReadOnly) }));
        for (; ; ) {
          const next = offset + rows.length;
          content = JSON.stringify({ tools: rows, next_cursor: next < candidates.length ? cursor(next, scope) : null });
          if (await this.counter(content) <= 1024 && (rows.length || offset === candidates.length)) break;
          if (!rows.length) fail("Search page cannot fit the model context budget.");
          rows.pop();
        }
        break;
      }
      case "inspect_tool": {
        const tool = this.tool(str("name"), registry);
        content = await this.page(JSON.stringify(tool), `inspect:${tool.name}`, str("cursor"), { tool: tool.name, format: "JSON path entries; read all pages for the complete contract" });
        break;
      }
      case "invoke_tool": {
        const tool = this.tool(str("name"), registry);
        const args = decodeChatToolArguments(str("arguments_json"));
        _ChatToolDiscovery.validate(args, tool.parameters);
        const revision = this.scopeRevision;
        const outcome = registry.executionOutcome ? await registry.executionOutcome({ ...request, toolName: tool.name, arguments: args }) : { type: "completed", result: await registry.execute({ ...request, toolName: tool.name, arguments: args }) };
        if (outcome.type !== "completed") return fail("This target requires direct foreground input and cannot be invoked through discovery.");
        if (revision !== this.scopeRevision) fail("Conversation changed during tool execution. The action must not be repeated automatically.");
        const stored = { userID: this.userID, conversationID: this.conversationID, handle: `result-${request.callId}`, targetTool: tool.name, content: outcome.result.content, isError: outcome.result.isError };
        this.results.set(stored.handle, stored);
        return { callId: request.callId, toolName: request.toolName, content: stored.content, isError: outcome.result.isError };
      }
      case "read_tool_result": {
        const stored = this.results.get(str("handle")) ?? fail("Result handle is unavailable in this user and conversation.");
        content = await this.resultPage(stored, str("cursor"));
        break;
      }
      default:
        return fail("Unknown discovery tool.");
    }
    return { callId: request.callId, toolName: request.toolName, content, isError: false };
  }
  async projectedContent(message) {
    if (message.toolCallId && this.excludedCallIDs.has(message.toolCallId)) return "This result belongs to a previous conversation or account and is unavailable.";
    const stored = message.toolCallId ? this.results.get(`result-${message.toolCallId}`) : void 0;
    if (stored) return this.resultPage(stored, "");
    return await this.counter(message.content) > 1024 ? this.page(message.content, "legacy", "", { notice: "Older result omitted after first page; original remains in conversation history" }) : message.content;
  }
  resultPage(result, supplied) {
    return this.page(result.content, result.handle, supplied, { handle: result.handle, tool: result.targetTool, ...result.isError !== void 0 && { is_error: String(result.isError) } });
  }
  async page(text, scope, supplied, metadata) {
    const entries = [];
    const priority = ["ok", "success", "isError", "error", "status", "approvalId", "requiresApproval", "tool", "name", "required"];
    const rank = (key) => priority.includes(key) ? priority.indexOf(key) : /id$|cursor|handle|continuation/i.test(key) ? priority.length : priority.length + 1;
    const visit = (value2, path) => {
      if (object(value2) && Object.keys(value2).length) {
        for (const key of Object.keys(value2).sort((a, b) => rank(a) - rank(b) || a.localeCompare(b))) visit(value2[key], `${path}/${key.replaceAll("~", "~0").replaceAll("/", "~1")}`);
      } else if (Array.isArray(value2) && value2.length) value2.forEach((v, i) => visit(v, `${path}/${i}`));
      else if (typeof value2 === "string" && [...value2].length > 256) {
        const chars = [...value2];
        for (let offset = 0; offset < chars.length; offset += 256) entries.push({ path, text_offset: offset, text_length: chars.length, value: chars.slice(offset, offset + 256).join("") });
      } else entries.push({ path, value: value2 });
    };
    let value;
    try {
      value = JSON.parse(text);
    } catch {
      value = text;
    }
    if (!supplied) {
      const whole = JSON.stringify({ ...metadata, content: value, next_cursor: null });
      if (await this.counter(whole) <= 1024) return whole;
    }
    visit(value, "");
    const start = cursorOffset(supplied, scope);
    if (start > entries.length) fail("Page cursor exceeds result size.");
    let low = start, high = Math.min(entries.length, start + 64), best;
    while (low <= high) {
      const end = Math.floor((low + high) / 2);
      const candidate = JSON.stringify({ ...metadata, entries: entries.slice(start, end), next_cursor: end < entries.length ? cursor(end, scope) : null });
      if (await this.counter(candidate) <= 1024) {
        best = candidate;
        low = end + 1;
      } else high = end - 1;
    }
    if (best === void 0 || high <= start && start !== entries.length) return fail("Result page envelope exceeds the model token budget.");
    return best;
  }
};
function cursor(offset, scope) {
  const bytes = new TextEncoder().encode(`${scope}
${offset}`);
  return btoa(Array.from(bytes, (b) => String.fromCharCode(b)).join(""));
}
function cursorOffset(value, scope) {
  if (!value) return 0;
  try {
    const decoded = new TextDecoder("utf-8", { fatal: true }).decode(Uint8Array.from(atob(value), (c) => c.charCodeAt(0)));
    const suffix = decoded.slice(scope.length + 1);
    if (decoded.startsWith(`${scope}
`) && /^\d+$/.test(suffix) && Number.isSafeInteger(Number(suffix))) return Number(suffix);
  } catch {
  }
  return fail("Invalid page cursor.");
}
function errorContent(content) {
  try {
    const v = JSON.parse(content);
    if (!object(v)) return void 0;
    if (v["isError"] === true || v["success"] === false || v["ok"] === false || v["ok"] === "false" || typeof v["error"] === "string" && v["error"].trim()) return true;
    if (v["success"] === true || v["ok"] === true || v["ok"] === "true") return false;
  } catch {
  }
  return void 0;
}

// src/axon-adapter/context-projection.ts
function projectedMessage(message, content = message.content) {
  return { ...message, content, providerMetadata: void 0 };
}
function removingOldestCompletedTurn(messages, protectedIDs = /* @__PURE__ */ new Set()) {
  const users = messages.flatMap((m, index) => m.role === "user" ? [index] : []);
  const remove = (start, end) => messages.filter((m, i) => i < start || i >= end || m.role === "system").map((m) => projectedMessage(m));
  for (let position = 0; position < users.length - 1; position += 1) {
    const start = users[position];
    const end = users[position + 1];
    if (!messages.slice(start, end).some((m) => protectedIDs.has(m.id))) return remove(start, end);
  }
  const assistants = messages.flatMap((m, i) => m.role === "assistant" && m.toolCalls?.length ? [i] : []);
  const lastInvocation = assistants.filter((i) => messages[i].toolCalls.some((c) => c.name === "invoke_tool")).at(-1) ?? -1;
  for (const index of assistants.slice(0, -1)) {
    const message = messages[index];
    if (index > lastInvocation && message.toolCalls.some((c) => c.name === "inspect_tool")) continue;
    const ids = new Set(message.toolCalls.map((c) => c.id));
    const found = /* @__PURE__ */ new Set();
    let end = index + 1;
    while (end < messages.length) {
      const result = messages[end];
      if (result.role !== "tool" || !result.toolCallId || !ids.has(result.toolCallId)) break;
      found.add(result.toolCallId);
      end += 1;
    }
    if (found.size === ids.size && !messages.slice(index, end).some((m) => protectedIDs.has(m.id))) return remove(index, end);
  }
  return null;
}
var ChatTurnContextProjection = class {
  contextsByMessageID = /* @__PURE__ */ new Map();
  project(messages, newContext, priorMessageIDs) {
    const anchor = messages.filter((m) => m.role === "user" && !priorMessageIDs.has(m.id)).at(-1);
    if (newContext !== void 0 && anchor && !this.contextsByMessageID.has(anchor.id)) {
      this.contextsByMessageID.set(anchor.id, newContext);
    }
    return messages.map((m) => {
      const context = m.role === "user" ? this.contextsByMessageID.get(m.id) : void 0;
      return context !== void 0 ? { ...m, content: m.content + context } : m;
    });
  }
  clear() {
    this.contextsByMessageID.clear();
  }
};

// src/axon-adapter/tool-activity-projection.ts
function axonMessagesToMultiBarChatMessages(messages, options = {}) {
  return messages.flatMap(
    (message) => axonMessageToMultiBarChatMessages(message, options)
  );
}
function axonMessagesToMultiBarTimeline(messages, options = {}) {
  return MultiBarToolActivityProjection.timeline(
    axonMessagesToMultiBarChatMessages(messages, options)
  );
}
function axonMessageToMultiBarChatMessages(message, options = {}) {
  const toolCalls = message.role === "assistant" ? message.toolCalls ?? [] : [];
  if (toolCalls.length > 0) {
    const projected = [];
    if (message.content.trim().length > 0) {
      projected.push(normalTranscriptMessage(message, options));
    }
    const callID = toolCalls.map((call) => call.id).join("|");
    projected.push(
      createMultiBarChatMessage({
        id: `${message.id}-tool-activity`,
        role: "assistant",
        content: activityLabel(
          toolCalls.map((call) => call.name),
          callID,
          options
        ),
        displayKind: "toolActivity",
        toolActivityEvent: {
          type: "calls",
          calls: toolCalls.map((call) => ({
            id: call.id,
            toolName: call.name,
            activityLabel: activityLabel([call.name], call.id, options),
            arguments: call.arguments
          }))
        }
      })
    );
    return projected;
  }
  if (message.role === "tool") {
    const toolName = nonEmpty(message.toolName) ?? "app_tool";
    const callID = nonEmpty(message.toolCallId);
    const approval = options.approvalForResult?.(message);
    return [
      createMultiBarChatMessage({
        id: `${message.id}-tool-result`,
        role: "tool",
        content: message.content,
        displayKind: "toolActivity",
        toolActivityEvent: {
          type: "result",
          ...callID !== void 0 && { callID },
          toolName,
          activityLabel: activityLabel(
            [toolName],
            callID ?? message.id,
            options
          ),
          payload: message.content,
          isError: options.resultIsError?.(message) ?? toolResultPayloadIsError(message.content),
          ...approval !== void 0 && { approval }
        }
      })
    ];
  }
  return [normalTranscriptMessage(message, options)];
}
function toolResultPayloadIsError(content) {
  let parsed;
  try {
    parsed = JSON.parse(content);
  } catch {
    return false;
  }
  if (!isRecord(parsed)) {
    return false;
  }
  if (parsed["isError"] === true) {
    return true;
  }
  if (parsed["success"] === false) {
    return true;
  }
  if (parsed["ok"] === false) {
    return true;
  }
  if (typeof parsed["ok"] === "string" && ["false", "0", "no"].includes(parsed["ok"].trim().toLowerCase())) {
    return true;
  }
  return typeof parsed["error"] === "string" && parsed["error"].trim().length > 0;
}
function normalTranscriptMessage(message, options) {
  return createMultiBarChatMessage({
    id: message.id,
    role: message.role,
    content: message.content,
    attachments: (message.attachments ?? []).map(
      axonAttachmentToAttachmentMetadata
    ),
    failed: message.role === "user" && message.id === options.failedMessageID
  });
}
function activityLabel(toolNames, callID, options) {
  return options.activityLabel?.(toolNames, callID) ?? MultiBarToolActivityLabeler.label(toolNames, callID);
}
function nonEmpty(value) {
  const trimmed = value?.trim();
  return trimmed === void 0 || trimmed.length === 0 ? void 0 : trimmed;
}
function isRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

// src/axon-adapter/axon-multi-bar-chat-state.ts
var AxonChatStateError = class _AxonChatStateError extends Error {
  kind;
  maxRounds;
  reason;
  constructor(kind, message, maxRounds, reason) {
    super(message);
    this.name = "AxonChatStateError";
    this.kind = kind;
    this.maxRounds = maxRounds;
    this.reason = reason;
  }
  static notConfigured() {
    return new _AxonChatStateError(
      "notConfigured",
      "Chat state is not configured. Call configure() or configureCustomProvider() first."
    );
  }
  static toolRoundLimitExceeded(maxRounds) {
    return new _AxonChatStateError(
      "toolRoundLimitExceeded",
      `Maximum tool continuation rounds exceeded (${maxRounds}).`,
      maxRounds
    );
  }
  static chatBusy() {
    return new _AxonChatStateError(
      "chatBusy",
      "The chat is still busy. Wait for the current response to finish."
    );
  }
  static noPendingUserInput() {
    return new _AxonChatStateError(
      "noPendingUserInput",
      "There is no pending questionnaire to resolve."
    );
  }
  static invalidPendingUserInput(reason) {
    const base = "The questionnaire does not match an unresolved tool call in this conversation.";
    return new _AxonChatStateError(
      "invalidPendingUserInput",
      reason === void 0 ? base : `${base} ${reason}`,
      void 0,
      reason
    );
  }
  static invalidUserInputResponse(reason) {
    return new _AxonChatStateError(
      "invalidUserInputResponse",
      `The questionnaire response is invalid: ${reason}`,
      void 0,
      reason
    );
  }
};
var AxonMultiBarChatState = class {
  static removingOldestCompletedTurn = removingOldestCompletedTurn;
  // MARK: - Configuration (mirrors the Swift @Published config vars)
  /** System prompt for the conversation. */
  systemPrompt = "You are a helpful assistant.";
  /** Maximum tokens for responses. */
  maxTokens = 4096;
  /** Maximum number of consecutive tool-result continuation rounds. */
  maxToolRounds = 4;
  /** Maximum automatic retries for transient streaming failures (timeouts, drops, 429/5xx). */
  maxAutoRetries = 2;
  /** Backoff delays (seconds) between automatic retries. */
  retryBackoff = [1, 3];
  /** Seconds without any streaming event before the stream is failed as stalled. */
  streamInactivityTimeout = 90;
  /** Seconds before a single tool execution is abandoned as timed out. */
  toolExecutionTimeout = 60;
  /** Stable conversation key for provider cache routing. */
  promptCacheKey;
  /** Request-only projection; throwing prevents the provider request. */
  requestPreflight;
  protectedContextMessageIDsProvider;
  pendingTokenUsage = null;
  pendingProviderMetadata;
  toolExposurePolicy = "fullCatalog";
  providerContextUsage;
  auxiliaryToolRounds = 0;
  discovery = new ChatToolDiscovery(async (text) => {
    if (!this.appleRuntime) throw new Error("Apple tool discovery requires the native token counting runtime.");
    return this.appleRuntime.countTokens(text);
  });
  appleRuntime;
  get usesDiscovery() {
    return this.toolExposurePolicy === "appleDiscovery" && this.provider === AIProvider.appleIntelligence && this.appleRuntime?.supportsExternalToolCalling(this.model ?? "") === true;
  }
  configureToolDiscoveryScope(userID, conversationID) {
    this.discovery.configureScope(userID, conversationID, this.messagesValue);
    this.providerContextUsage = void 0;
  }
  rehomeToolDiscoveryScope(conversationID) {
    this.discovery.rehome(conversationID, this.messagesValue);
  }
  // MARK: - Private state
  provider = null;
  model = null;
  apiKey = null;
  customBaseUrl = null;
  settings = void 0;
  toolRegistry = null;
  streamingClient;
  isStreamingValue = false;
  isPausedValue = false;
  streamingContentValue = "";
  messagesValue = [];
  errorValue = null;
  failedUserMessageIDValue = null;
  lastTokenUsageValue = null;
  pendingUserInputValue = null;
  pendingUserInputNextToolRoundCount = 0;
  toolResultErrorsByMessageID = /* @__PURE__ */ new Map();
  currentRun = null;
  snapshot;
  listeners = /* @__PURE__ */ new Set();
  notifyScheduled = false;
  constructor(options = {}) {
    this.streamingClient = options.streamingClient ?? new StreamingClient();
    this.appleRuntime = options.appleToolDiscoveryRuntime;
    if (options.systemPrompt !== void 0) {
      this.systemPrompt = options.systemPrompt;
    }
    this.snapshot = this.buildSnapshot();
  }
  // MARK: - Store contract
  subscribe(listener) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
  getSnapshot() {
    return this.snapshot;
  }
  // MARK: - Configuration methods
  /** Configure for a built-in AI provider. */
  configure(options) {
    this.provider = options.provider;
    this.model = options.model;
    this.apiKey = options.apiKey;
    this.settings = options.settings;
    this.customBaseUrl = null;
  }
  /** Configure for a custom OpenAI-compatible provider. */
  configureCustomProvider(options) {
    this.provider = null;
    this.customBaseUrl = options.baseUrl;
    this.model = options.model;
    this.apiKey = options.apiKey;
    this.settings = options.settings;
  }
  /** Configure provider-neutral app tools for model function calling. */
  configureTools(registry) {
    this.toolRegistry = registry;
  }
  /** Whether the chat is ready to send messages (configured with provider/model/key). */
  get isConfigured() {
    return this.model !== null && this.apiKey !== null && (this.provider !== null || this.customBaseUrl !== null);
  }
  /** The current model name. */
  get currentModelName() {
    return this.model;
  }
  /** The current provider name. */
  get currentProviderName() {
    if (this.provider !== null) {
      return providerDisplayName(this.provider);
    }
    return this.customBaseUrl !== null ? "Custom" : null;
  }
  /** The last assistant message in the conversation. */
  get lastAssistantMessage() {
    return findLast(this.messagesValue, (message) => message.role === "assistant");
  }
  /** The last user message in the conversation. */
  get lastUserMessage() {
    return findLast(this.messagesValue, (message) => message.role === "user");
  }
  /** Foreground questionnaire currently blocking ordinary chat continuation. */
  get pendingUserInput() {
    return this.pendingUserInputValue;
  }
  /** Whether a foreground questionnaire is waiting for its host-owned response. */
  get isAwaitingUserInput() {
    return this.pendingUserInputValue !== null;
  }
  /**
   * Projects provider messages into the package's headless transcript render
   * model. In-session tool errors use their authoritative execution result.
   */
  toolActivityMessages(options = {}) {
    return axonMessagesToMultiBarChatMessages(
      this.messagesValue,
      this.resolvedToolActivityOptions(options)
    );
  }
  /**
   * Returns normal transcript messages interleaved with maximal contiguous tool
   * traces. Consumers can render these with their framework of choice.
   */
  toolActivityTimeline(options = {}) {
    return axonMessagesToMultiBarTimeline(
      this.messagesValue,
      this.resolvedToolActivityOptions(options)
    );
  }
  // MARK: - Message management
  /** Add a system message to the conversation. */
  addSystemMessage(content) {
    this.messagesValue = [...this.messagesValue, createMessage({ role: "system", content })];
    this.markChanged();
  }
  /** Clear conversation history. */
  clearHistory() {
    this.lastTokenUsageValue = null;
    this.messagesValue = [];
    this.discovery.restore([]);
    this.auxiliaryToolRounds = 0;
    this.providerContextUsage = void 0;
    this.toolResultErrorsByMessageID.clear();
    this.streamingContentValue = "";
    this.pendingProviderMetadata = void 0;
    this.pendingTokenUsage = null;
    this.errorValue = null;
    this.failedUserMessageIDValue = null;
    this.pendingUserInputValue = null;
    this.pendingUserInputNextToolRoundCount = 0;
    this.markChanged();
  }
  /**
   * Restore messages from a saved conversation (e.g., loading a thread).
   * Replaces the current message history with the provided messages.
   */
  restoreMessages(savedMessages) {
    this.lastTokenUsageValue = null;
    this.messagesValue = [...savedMessages];
    this.discovery.restore(savedMessages);
    this.auxiliaryToolRounds = 0;
    this.providerContextUsage = void 0;
    this.toolResultErrorsByMessageID.clear();
    this.streamingContentValue = "";
    this.pendingProviderMetadata = void 0;
    this.pendingTokenUsage = null;
    this.errorValue = null;
    this.failedUserMessageIDValue = null;
    this.pendingUserInputValue = null;
    this.pendingUserInputNextToolRoundCount = 0;
    this.markChanged();
  }
  /** Restore a validated unresolved foreground request without contacting a provider. */
  restorePendingUserInput(request, nextToolRoundCount = 1) {
    if (this.isStreamingValue || this.currentRun !== null) {
      throw AxonChatStateError.chatBusy();
    }
    if (this.pendingUserInputValue !== null) {
      throw AxonChatStateError.invalidPendingUserInput(
        "Another questionnaire is already pending."
      );
    }
    let validatedRequest;
    try {
      validatedRequest = validateChatToolUserInputRequest(request);
    } catch (error) {
      throw AxonChatStateError.invalidPendingUserInput(describeError(error));
    }
    const trailingMessage = this.messagesValue.at(-1);
    const trailingCalls = trailingMessage?.role === "assistant" ? trailingMessage.toolCalls ?? [] : [];
    const hasMatchingCall = trailingCalls.length === 1 && trailingCalls[0]?.id === validatedRequest.callId && trailingCalls[0]?.name === validatedRequest.toolName;
    const hasMatchingResult = this.messagesValue.some(
      (message) => message.role === "tool" && message.toolCallId === validatedRequest.callId
    );
    if (!hasMatchingCall || hasMatchingResult) {
      throw AxonChatStateError.invalidPendingUserInput();
    }
    this.pendingUserInputValue = validatedRequest;
    if (this.usesDiscovery) {
      const lastUser = this.messagesValue.map((m) => m.role).lastIndexOf("user");
      const batches = this.messagesValue.slice(lastUser + 1).flatMap((m) => m.toolCalls?.length ? [m.toolCalls] : []);
      this.auxiliaryToolRounds = batches.filter((calls) => calls.some((c) => ChatToolDiscovery.auxiliaryNames.has(c.name))).length;
      this.pendingUserInputNextToolRoundCount = Math.max(1, batches.filter((calls) => calls.some((c) => !ChatToolDiscovery.auxiliaryNames.has(c.name))).length);
    } else {
      this.pendingUserInputNextToolRoundCount = normalizeNextToolRoundCount(nextToolRoundCount);
    }
    this.markChanged();
  }
  /** Append the matching tool result and resume the suspended model turn. */
  resolvePendingUserInput(response) {
    if (this.isStreamingValue || this.currentRun !== null) {
      throw AxonChatStateError.chatBusy();
    }
    const request = this.pendingUserInputValue;
    if (request === null) {
      throw AxonChatStateError.noPendingUserInput();
    }
    let validatedResponse;
    let content;
    try {
      validatedResponse = validateChatToolUserInputResponse(response, request);
      content = encodeChatToolUserInputResultEnvelope(request, validatedResponse);
    } catch (error) {
      throw AxonChatStateError.invalidUserInputResponse(describeError(error));
    }
    const nextToolRoundCount = this.pendingUserInputNextToolRoundCount;
    this.pendingUserInputValue = null;
    this.pendingUserInputNextToolRoundCount = 0;
    this.appendToolResult({
      callId: request.callId,
      toolName: request.toolName,
      content,
      isError: false
    });
    this.startStreamingFromToolRound(nextToolRoundCount);
  }
  /** Cancel the pending questionnaire and resume with a cancelled tool result. */
  cancelPendingUserInput() {
    const request = this.pendingUserInputValue;
    if (request === null) {
      throw AxonChatStateError.noPendingUserInput();
    }
    this.resolvePendingUserInput(
      createCancelledChatToolUserInputResponse(request.id)
    );
  }
  // MARK: - Core chat operations
  /** Send a user message (optionally with attachments) and begin streaming the response. */
  send(message, attachments = []) {
    if (message.trim().length === 0 && attachments.length === 0) {
      return;
    }
    if (this.pendingUserInputValue !== null) {
      console.warn(
        "AxonMultiBarChatState: blocked send while foreground user input is pending"
      );
      return;
    }
    if (this.isStreamingValue || this.currentRun !== null) {
      console.warn("AxonMultiBarChatState: blocked send while chat stream is active");
      return;
    }
    const userMessage = createMessage({
      role: "user",
      content: message,
      ...attachments.length > 0 && { attachments }
    });
    this.messagesValue = [...this.messagesValue, userMessage];
    this.markChanged();
    this.startStreaming();
  }
  /** Stop/cancel the current streaming response. */
  stop() {
    const run = this.currentRun;
    this.currentRun = null;
    run?.controller.abort();
    if (this.streamingContentValue.length > 0) {
      this.finalizeAssistantMessage();
    }
    this.isStreamingValue = false;
    this.isPausedValue = false;
    this.markChanged();
  }
  /** Pause the current streaming. */
  pause() {
    this.isPausedValue = true;
    this.markChanged();
  }
  /** Resume a paused stream. */
  resume() {
    this.isPausedValue = false;
    this.markChanged();
  }
  /**
   * Interrupt the agent with a user interjection.
   * Stops the current stream and allows the user to send a follow-up.
   */
  interrupt() {
    this.stop();
  }
  /** Regenerate the last assistant response by re-sending the last user message. */
  regenerate() {
    if (this.pendingUserInputValue !== null) {
      console.warn(
        "AxonMultiBarChatState: blocked regenerate while foreground user input is pending"
      );
      return;
    }
    this.messagesValue = removingLastResponseAttempt(this.messagesValue);
    this.pruneToolResultErrors();
    this.markChanged();
    if (this.messagesValue.some((message) => message.role === "user")) {
      this.startStreaming();
    }
  }
  // MARK: - Streaming implementation
  startStreaming() {
    this.startStreamingFromToolRound(0);
  }
  startStreamingFromToolRound(initialToolRoundCount) {
    if (this.pendingUserInputValue !== null) {
      console.warn(
        "AxonMultiBarChatState: blocked streaming start while foreground user input is pending"
      );
      return;
    }
    if (this.isStreamingValue || this.currentRun !== null) {
      console.warn("AxonMultiBarChatState: blocked duplicate streaming start");
      return;
    }
    this.isStreamingValue = true;
    this.isPausedValue = false;
    this.streamingContentValue = "";
    this.pendingProviderMetadata = void 0;
    this.pendingTokenUsage = null;
    this.errorValue = null;
    this.failedUserMessageIDValue = null;
    this.markChanged();
    const run = { controller: new AbortController() };
    this.currentRun = run;
    if (initialToolRoundCount === 0) this.auxiliaryToolRounds = 0;
    void this.performStreaming(run, initialToolRoundCount);
  }
  async performStreaming(run, initialToolRoundCount) {
    const signal = run.controller.signal;
    try {
      await this.runStreamingWithRetry(run, initialToolRoundCount);
      if (this.errorValue !== null) {
        this.markLastUserMessageFailed();
      }
    } catch (error) {
      if (signal.aborted) {
        return;
      }
      if (isInvalidToolOrderingError(error)) {
        const originalError = error;
        this.messagesValue = sanitizedForProviderContext(this.messagesValue);
        this.pruneToolResultErrors();
        this.streamingContentValue = "";
        this.markChanged();
        try {
          await this.runStreamingWithRetry(
            run,
            initialToolRoundCount,
            recoverySteeringPrompt
          );
          return;
        } catch {
          this.errorValue = originalError;
        }
      } else {
        this.errorValue = error;
        if (this.streamingContentValue.length > 0) {
          this.finalizeAssistantMessage();
        }
      }
      this.markLastUserMessageFailed();
    } finally {
      if (this.currentRun === run) {
        this.currentRun = null;
        this.isStreamingValue = false;
        this.isPausedValue = false;
      }
      this.markChanged();
    }
  }
  /**
   * Runs the streaming loop, automatically retrying transient failures with backoff
   * as long as no meaningful response content has arrived yet.
   */
  async runStreamingWithRetry(run, initialToolRoundCount, systemPromptSuffix) {
    const signal = run.controller.signal;
    let attempt = 0;
    for (; ; ) {
      const messageCountBefore = this.messagesValue.length;
      try {
        await this.runStreamingLoop(
          run,
          initialToolRoundCount,
          systemPromptSuffix
        );
        return;
      } catch (error) {
        const madeProgress = this.streamingContentValue.length > 0 || this.messagesValue.length > messageCountBefore;
        if (signal.aborted || madeProgress || attempt >= this.maxAutoRetries || !isRetryableStreamError(error)) {
          throw error;
        }
        const backoffSeconds = this.retryBackoff.length === 0 ? 1 : this.retryBackoff[Math.min(attempt, this.retryBackoff.length - 1)] ?? 1;
        attempt += 1;
        console.warn(
          `AxonMultiBarChatState: transient streaming failure (${describeError(error)}) \u2014 retrying in ${backoffSeconds}s (attempt ${attempt}/${this.maxAutoRetries})`
        );
        await sleep(backoffSeconds * 1e3);
        this.errorValue = null;
        this.markChanged();
      }
    }
  }
  markLastUserMessageFailed() {
    this.failedUserMessageIDValue = this.lastUserMessage?.id ?? null;
    this.markChanged();
  }
  async runStreamingLoop(run, initialToolRoundCount, systemPromptSuffix) {
    const signal = run.controller.signal;
    let toolRoundCount = initialToolRoundCount;
    const scopeRevision = this.discovery.scopeRevision;
    while (!signal.aborted) {
      if (this.usesDiscovery && this.discovery.scopeRevision !== scopeRevision) throw new Error("Conversation scope changed.");
      const stream = await this.makeStream(run, systemPromptSuffix);
      let completedToolCalls = [];
      let needsToolResults = false;
      for await (const event of stream) {
        if (signal.aborted) {
          break;
        }
        while (this.isPausedValue && !signal.aborted) {
          await sleep(100);
        }
        if (signal.aborted) {
          break;
        }
        switch (event.type) {
          case "toolCallComplete": {
            if (!completedToolCalls.some((toolCall) => toolCall.id === event.toolCall.id)) {
              completedToolCalls.push(event.toolCall);
            }
            break;
          }
          case "toolResultRequired": {
            completedToolCalls = [...event.toolCalls];
            needsToolResults = true;
            break;
          }
          case "completion": {
            const toolCalls = event.completion.toolCalls;
            if (toolCalls && toolCalls.length > 0) {
              completedToolCalls = [...toolCalls];
              needsToolResults = true;
            }
            this.handleStreamEvent(event);
            break;
          }
          default:
            this.handleStreamEvent(event);
        }
      }
      if (signal.aborted) {
        break;
      }
      if (needsToolResults && completedToolCalls.length > 0) {
        this.appendAssistantToolCallMessage(completedToolCalls);
        const hasAuxiliary = this.usesDiscovery && completedToolCalls.some((c) => ChatToolDiscovery.auxiliaryNames.has(c.name));
        const hasBusiness = !this.usesDiscovery || completedToolCalls.some((c) => !ChatToolDiscovery.auxiliaryNames.has(c.name));
        if (hasBusiness && toolRoundCount >= this.maxToolRounds || hasAuxiliary && this.auxiliaryToolRounds >= 8) {
          this.appendToolRoundLimitResults(completedToolCalls);
          this.errorValue = AxonChatStateError.toolRoundLimitExceeded(hasAuxiliary && this.auxiliaryToolRounds >= 8 ? 8 : this.maxToolRounds);
          this.markChanged();
          break;
        }
        const registry = this.toolRegistry;
        if (!registry) {
          this.appendMissingRegistryResults(completedToolCalls);
          break;
        }
        const definitionsByName = new Map(
          (this.usesDiscovery ? this.discovery.definitionsFor(registry) : registry.toolDefinitions).map((definition) => [definition.name, definition])
        );
        const mixedBatchResults = foregroundUserInputBatchRejectionResults(
          completedToolCalls,
          definitionsByName
        );
        if (mixedBatchResults !== null) {
          for (const result of mixedBatchResults) {
            this.appendToolResult(result, false);
          }
          this.markChanged();
          if (hasBusiness) toolRoundCount += 1;
          if (hasAuxiliary) this.auxiliaryToolRounds += 1;
          continue;
        }
        for (const toolCall of completedToolCalls) {
          let rawOutcome;
          const request = toolCallToChatToolRequest(toolCall);
          if (request) {
            try {
              rawOutcome = await withTimeout(this.toolExecutionTimeout, async () => {
                if (this.usesDiscovery && ChatToolDiscovery.names.has(request.toolName)) {
                  return { type: "completed", result: await this.discovery.execute(request, registry) };
                }
                if (this.usesDiscovery && !definitionsByName.has(request.toolName)) {
                  return { type: "completed", result: chatToolErrorResult(request.callId, request.toolName, "Use search_tools, inspect_tool, and invoke_tool for application tools.") };
                }
                return registry.executionOutcome ? registry.executionOutcome(request) : { type: "completed", result: await registry.execute(request) };
              });
            } catch (error) {
              rawOutcome = {
                type: "completed",
                result: chatToolErrorResult(
                  toolCall.id,
                  toolCall.name,
                  this.usesDiscovery ? describeError(error) : `Tool execution timed out after ${Math.trunc(this.toolExecutionTimeout)} seconds.`
                )
              };
            }
          } else {
            console.warn(
              `AxonMultiBarChatState: malformed tool-call arguments for ${toolCall.name} (call ${toolCall.id}); returning tool error instead of executing with empty arguments`
            );
            rawOutcome = {
              type: "completed",
              result: chatToolErrorResult(
                toolCall.id,
                toolCall.name,
                "The tool call arguments were malformed or truncated during streaming and could not be parsed. Issue the tool call again with complete JSON arguments."
              )
            };
          }
          if (signal.aborted) return;
          if (this.usesDiscovery && this.discovery.scopeRevision !== scopeRevision) throw new Error("Conversation scope changed during tool execution; do not repeat the action.");
          const outcome = validatedExecutionOutcome(rawOutcome, toolCall);
          if (outcome === null) {
            this.appendToolResult(
              chatToolErrorResult(
                toolCall.id,
                toolCall.name,
                "The tool returned an invalid execution outcome."
              )
            );
            continue;
          }
          if (outcome.type === "completed") {
            this.appendToolResult(outcome.result);
            continue;
          }
          let pendingRequest;
          try {
            pendingRequest = validateChatToolUserInputRequest(outcome.request);
          } catch (error) {
            this.appendToolResult(
              chatToolErrorResult(
                toolCall.id,
                toolCall.name,
                `The tool returned an invalid foreground user-input request. ${describeError(error)}`
              )
            );
            continue;
          }
          const definition = definitionsByName.get(toolCall.name);
          if (definition?.executionMode !== "foregroundUserInput" || pendingRequest.callId !== toolCall.id || pendingRequest.toolName !== toolCall.name) {
            this.appendToolResult(
              chatToolErrorResult(
                toolCall.id,
                toolCall.name,
                "The tool returned an invalid foreground user-input request."
              )
            );
            continue;
          }
          this.pendingUserInputValue = pendingRequest;
          this.pendingUserInputNextToolRoundCount = toolRoundCount + 1;
          return;
        }
        if (hasBusiness) toolRoundCount += 1;
        if (hasAuxiliary) this.auxiliaryToolRounds += 1;
        continue;
      }
      this.finalizeAssistantMessage();
      break;
    }
  }
  async makeStream(run, systemPromptSuffix) {
    const scopeRevision = this.discovery.scopeRevision;
    const enabledTools = this.provider !== null && providerIsOnDevice(this.provider) && !(this.provider === AIProvider.appleIntelligence && this.appleRuntime?.supportsExternalToolCalling(this.model ?? "")) ? [] : (this.toolRegistry ? this.usesDiscovery ? this.discovery.definitionsFor(this.toolRegistry) : this.toolRegistry.toolDefinitions : []).filter((definition) => definition.isEnabled).map(chatToolDefinitionToAxonTool);
    let providerMessages = sanitizedForProviderContext(this.messagesValue);
    const resolvedSystemPrompt = [this.systemPrompt, systemPromptSuffix, this.usesDiscovery ? ChatToolDiscovery.guidance : void 0].map((part) => part?.trim() ?? "").filter((part) => part.length > 0).join("\n\n");
    if (this.usesDiscovery) {
      if (!this.discovery.hasScope) throw new Error("Apple tool discovery requires an authenticated conversation scope.");
      this.discovery.rebuild(this.messagesValue);
      let rewritten = false;
      providerMessages = await Promise.all(providerMessages.map(async (message) => {
        if (message.role !== "tool") return message;
        const content = await this.discovery.projectedContent(message);
        if (content === message.content) return message;
        rewritten = true;
        return projectedMessage(message, content);
      }));
      if (rewritten) providerMessages = providerMessages.map((m) => m.role === "assistant" ? projectedMessage(m) : m);
      const originalCount = providerMessages.length;
      for (; ; ) {
        const budget = await this.appleRuntime.preflight(providerMessages, resolvedSystemPrompt, enabledTools, this.maxTokens, 256);
        if (![budget.inputTokens, budget.contextSize, budget.responseReserve, budget.safetyMargin].every((v) => Number.isFinite(v) && v >= 0)) throw new Error("Invalid native context budget.");
        this.providerContextUsage = { ...budget, omittedMessageCount: originalCount - providerMessages.length };
        if (budget.inputTokens + budget.responseReserve + budget.safetyMargin <= budget.contextSize) break;
        const reduced = removingOldestCompletedTurn(providerMessages, this.protectedContextMessageIDsProvider?.());
        if (reduced === null) throw new Error("The essential Apple Intelligence context exceeds its token budget. Start a new conversation or shorten the current request. No action was repeated.");
        providerMessages = reduced;
      }
    } else this.providerContextUsage = void 0;
    if (this.requestPreflight) {
      providerMessages = await this.requestPreflight(structuredClone(providerMessages), resolvedSystemPrompt, structuredClone(enabledTools), this.maxTokens);
    }
    if (run.controller.signal.aborted) return (async function* () {
    })();
    if (this.usesDiscovery && scopeRevision !== this.discovery.scopeRevision) throw new Error("Conversation scope changed during request projection.");
    if (this.customBaseUrl !== null && this.model !== null && this.apiKey !== null) {
      return withInactivityTimeout(
        this.streamInactivityTimeout,
        this.streamingClient.streamFromCustomProvider({
          messages: providerMessages,
          baseUrl: this.customBaseUrl,
          model: this.model,
          apiKey: this.apiKey,
          system: resolvedSystemPrompt,
          maxTokens: this.maxTokens,
          settings: this.settings,
          signal: run.controller.signal
        })
      );
    }
    if (this.provider !== null && this.model !== null && this.apiKey !== null) {
      return withInactivityTimeout(
        this.streamInactivityTimeout,
        this.streamingClient.stream({
          messages: providerMessages,
          provider: this.provider,
          model: this.model,
          apiKey: this.apiKey,
          system: resolvedSystemPrompt,
          maxTokens: this.maxTokens,
          settings: this.settings,
          tools: enabledTools.length === 0 ? void 0 : enabledTools,
          toolChoice: enabledTools.length === 0 ? void 0 : { type: "auto" },
          promptCacheKey: this.promptCacheKey,
          signal: run.controller.signal
        })
      );
    }
    throw AxonChatStateError.notConfigured();
  }
  handleStreamEvent(event) {
    switch (event.type) {
      case "textDelta":
        this.streamingContentValue += event.text;
        this.markChanged();
        break;
      case "reasoningDelta":
        break;
      case "completion":
        this.lastTokenUsageValue = event.completion.tokens ?? null;
        this.pendingTokenUsage = event.completion.tokens ?? null;
        this.pendingProviderMetadata = event.completion.providerMetadata;
        if (event.completion.fullContent.length > 0) {
          this.streamingContentValue = event.completion.fullContent;
        }
        this.markChanged();
        break;
      case "toolCallStart":
      case "toolCallDelta":
      case "toolCallComplete":
      case "toolResultRequired":
        break;
      case "error":
        this.errorValue = event.error;
        this.markChanged();
        break;
    }
  }
  finalizeAssistantMessage() {
    if (this.streamingContentValue.length === 0 && !this.pendingProviderMetadata?.responsesOutputJSON && !this.pendingProviderMetadata?.anthropicContentJSON && !this.pendingProviderMetadata?.appleResponseTranscriptJSON) return;
    const assistantMessage = createMessage({
      role: "assistant",
      content: this.streamingContentValue,
      ...this.pendingProviderMetadata !== void 0 && { providerMetadata: this.pendingProviderMetadata },
      ...this.pendingTokenUsage !== null && { tokens: this.pendingTokenUsage },
      ...this.model !== null && { modelName: this.model },
      providerName: this.provider !== null ? providerDisplayName(this.provider) : "Custom"
    });
    this.messagesValue = [...this.messagesValue, assistantMessage];
    this.streamingContentValue = "";
    this.pendingProviderMetadata = void 0;
    this.pendingTokenUsage = null;
    this.markChanged();
  }
  appendAssistantToolCallMessage(toolCalls) {
    const assistantMessage = createMessage({
      role: "assistant",
      content: this.streamingContentValue,
      ...this.pendingProviderMetadata !== void 0 && { providerMetadata: this.pendingProviderMetadata },
      ...this.pendingTokenUsage !== null && { tokens: this.pendingTokenUsage },
      ...this.model !== null && { modelName: this.model },
      providerName: this.provider !== null ? providerDisplayName(this.provider) : "Custom",
      toolCalls: [...toolCalls]
    });
    this.messagesValue = [...this.messagesValue, assistantMessage];
    this.streamingContentValue = "";
    this.pendingProviderMetadata = void 0;
    this.pendingTokenUsage = null;
    this.markChanged();
  }
  appendMissingRegistryResults(toolCalls) {
    for (const toolCall of toolCalls) {
      const result = chatToolErrorResult(
        toolCall.id,
        toolCall.name,
        "No tool registry is configured."
      );
      this.appendToolResult(result, false);
    }
    this.markChanged();
  }
  appendToolRoundLimitResults(toolCalls) {
    for (const toolCall of toolCalls) {
      const result = chatToolErrorResult(
        toolCall.id,
        toolCall.name,
        "Maximum tool continuation rounds exceeded."
      );
      this.appendToolResult(result, false);
    }
    this.markChanged();
  }
  appendToolResult(result, notify = true) {
    const message = toolResultMessage(
      result.callId,
      result.toolName,
      result.content
    );
    this.messagesValue = [...this.messagesValue, message];
    this.toolResultErrorsByMessageID.set(message.id, result.isError);
    if (notify) {
      this.markChanged();
    }
  }
  resolvedToolActivityOptions(options) {
    return {
      ...options,
      resultIsError: (message) => this.toolResultErrorsByMessageID.get(message.id) ?? options.resultIsError?.(message),
      failedMessageID: options.failedMessageID ?? this.failedUserMessageIDValue
    };
  }
  pruneToolResultErrors() {
    const retainedMessageIDs = new Set(
      this.messagesValue.map((message) => message.id)
    );
    for (const messageID of this.toolResultErrorsByMessageID.keys()) {
      if (!retainedMessageIDs.has(messageID)) {
        this.toolResultErrorsByMessageID.delete(messageID);
      }
    }
  }
  // MARK: - Snapshot / notification
  buildSnapshot() {
    return Object.freeze({
      isStreaming: this.isStreamingValue,
      isPaused: this.isPausedValue,
      streamingContent: this.streamingContentValue,
      messages: this.messagesValue,
      error: this.errorValue,
      failedUserMessageID: this.failedUserMessageIDValue,
      lastTokenUsage: this.lastTokenUsageValue,
      providerContextUsage: this.providerContextUsage,
      ...this.pendingUserInputValue !== null && {
        pendingUserInput: this.pendingUserInputValue
      }
    });
  }
  markChanged() {
    this.snapshot = this.buildSnapshot();
    if (this.notifyScheduled) {
      return;
    }
    this.notifyScheduled = true;
    queueMicrotask(() => {
      this.notifyScheduled = false;
      for (const listener of [...this.listeners]) {
        listener();
      }
    });
  }
};
function foregroundUserInputBatchRejectionResults(toolCalls, definitionsByName) {
  const containsForegroundUserInput = toolCalls.some(
    (toolCall) => definitionsByName.get(toolCall.name)?.executionMode === "foregroundUserInput"
  );
  if (!containsForegroundUserInput || toolCalls.length === 1) {
    return null;
  }
  return toolCalls.map(
    (toolCall) => chatToolErrorResult(
      toolCall.id,
      toolCall.name,
      "A foreground user-input tool must be the only tool in its round. No tools in this batch were executed. Retry the clarification tool alone, then wait for the user's response."
    )
  );
}
function validatedExecutionOutcome(value, toolCall) {
  if (!isPlainObjectValue(value)) {
    return null;
  }
  if (value["type"] === "completed") {
    const result = value["result"];
    if (!isPlainObjectValue(result) || result["callId"] !== toolCall.id || result["toolName"] !== toolCall.name || typeof result["content"] !== "string" || typeof result["isError"] !== "boolean") {
      return null;
    }
    return {
      type: "completed",
      result: {
        callId: result["callId"],
        toolName: result["toolName"],
        content: result["content"],
        isError: result["isError"]
      }
    };
  }
  if (value["type"] === "requiresUserInput") {
    const request = value["request"];
    if (!isPlainObjectValue(request)) {
      return null;
    }
    return {
      type: "requiresUserInput",
      request
    };
  }
  return null;
}
function isPlainObjectValue(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function normalizeNextToolRoundCount(value) {
  if (!Number.isFinite(value)) {
    return 1;
  }
  return Math.max(1, Math.trunc(value));
}
function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}
function describeError(error) {
  return error instanceof Error ? error.message : String(error);
}
function findLast(values, predicate) {
  for (let index = values.length - 1; index >= 0; index -= 1) {
    const value = values[index];
    if (value !== void 0 && predicate(value)) {
      return value;
    }
  }
  return void 0;
}

export { AxonChatService, AxonChatStateError, AxonMultiBarChatState, ChatToolDiscovery, ChatTurnContextProjection, attachmentMetadataToAxonAttachment, attachmentTypeToAxonAttachmentType, axonAttachmentToAttachmentMetadata, axonAttachmentTypeToAttachmentType, axonMessageToChatMessage, axonMessageToMultiBarChatMessages, axonMessagesToMultiBarChatMessages, axonMessagesToMultiBarTimeline, axonModelId, axonProviderForModel, axonProviderToModalProvider, axonRoleToMessageRole, axonToolCallToChatMetadata, chatMessageToAxonMessage, chatToolDefinitionToAxonTool, isInvalidToolOrderingError, isRetryableStreamError, messageRoleToAxonRole, projectedMessage, recoverySteeringPrompt, removingLastResponseAttempt, removingOldestCompletedTurn, sanitizedForProviderContext, streamingErrorToChatServiceError, toolCallToChatToolRequest, toolResultPayloadIsError, unknownErrorToChatServiceError, withInactivityTimeout, withTimeout };
//# sourceMappingURL=chunk-UI7W3CMM.js.map
//# sourceMappingURL=chunk-UI7W3CMM.js.map